import { useEffect, useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import type { MigrationSummary } from '@weave/schema-gen-ts/dist/schemas/integrations/sync-app-onprem-migrations/v1/message.pb';
import { useListMigrationsByStatusQueries, useListMigrationsQuery } from '@frontend/api-sync-app-on-prem-migrations';
import { useAlert } from '@frontend/design-system';
import MigrationDashboardView from '../../../../pages/MigrationDashboardView/MigrationDashboardView';
import { useIntegrationsDashboardShallowStore } from '../../../../store';
import { MIGRATION_STATUSES } from '../../../../utils/constants';
import { normalizeMigration } from '../../../../utils/normalizeMigration';

const DEFAULT_PAGE_SIZE = 100;

const MigrationDashboardPage = () => {
  const { setMigratedAtEntries } = useIntegrationsDashboardShallowStore('setMigratedAtEntries');
  const [page, setPage] = useState(1);
  const pageSize = DEFAULT_PAGE_SIZE;
  const alert = useAlert();

  const {
    data: listMigrationsData,
    isFetching: isTableFetching,
    isError: isTableError,
  } = useListMigrationsQuery({
    request: { pageSize, page },
  });

  const migrationsByStatusResults = useListMigrationsByStatusQueries(
    MIGRATION_STATUSES.map((status) => ({
      request: { status },
      options: {
        onError: () => {
          alert.warning({
            message: `Couldn't load data for: ${status}. Numbers below may be incomplete.`,
            autoDismissAfter: 5000,
          });
        },
      },
    }))
  );

  const tableMigrations = useMemo<MigrationSummary[]>(
    () => listMigrationsData?.pages.flatMap((listPage) => (listPage.migrations ?? []).map(normalizeMigration)) ?? [],
    [listMigrationsData]
  );

  // Cache each migration's `migratedAt` in the app store so the detail page
  // (whose GetMigrationByID response omits this field) can still display it.
  useEffect(() => {
    if (tableMigrations.length === 0) return;
    setMigratedAtEntries(tableMigrations.map(({ id, migratedAt }) => ({ id, migratedAt })));
  }, [tableMigrations]);

  const analyticsMigrations = useMemo<MigrationSummary[]>(
    () => migrationsByStatusResults.flatMap((result) => result.data?.migrations?.map(normalizeMigration) ?? []),
    [migrationsByStatusResults]
  );

  const isAnalyticsFetching = migrationsByStatusResults.some((r) => r.isFetching);

  const isFullPage = tableMigrations.length === pageSize;
  // The API has no total count, so estimate: if the current page is full, assume one more exists.
  // Once we hit a partial/empty page we know the exact upper bound.
  const totalPages = Math.max(1, isFullPage ? page + 1 : page);

  return (
    <MigrationDashboardView
      migrationList={tableMigrations}
      analyticsMigrationList={analyticsMigrations}
      isFetching={isTableFetching}
      isError={isTableError}
      isAnalyticsFetching={isAnalyticsFetching}
      paginationConfig={{
        page,
        pageSize,
        totalPages,
        hasNext: isFullPage,
        hasPrevious: page > 1,
        onPageChange: (next) => setPage(Math.max(1, Math.min(next, totalPages))),
      }}
    />
  );
};

export const Route = createFileRoute('/_auth/migrationdashboard/')({
  component: MigrationDashboardPage,
});
